import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";

const LOGO_URL = "/logo.jpeg";

const navItems = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/services", label: "Services" },
    { to: "/consultation", label: "Consultation" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    let headerBgClass = "bg-transparent";
    if (open) {
        headerBgClass = "bg-[#FAF9F6] border-b border-[#E3DCC8]";
    } else if (scrolled) {
        headerBgClass =
            "bg-[#FAF9F6]/90 backdrop-blur-md border-b border-[#E3DCC8]";
    }

    return (
        <header
            className={`sticky top-0 z-40 w-full transition-all duration-300 ${headerBgClass}`}
            data-testid="site-header"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20 md:h-24">
                    {/* Logo (slightly larger) */}
                    <Link
                        to="/"
                        className="flex items-center gap-3 group"
                        data-testid="nav-logo-link"
                        aria-label="Present Day Counseling and Recovery home"
                    >
                        <img
                            src={LOGO_URL}
                            alt="Present Day Counseling and Recovery logo"
                            className="h-16 md:h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                            data-testid="nav-logo-image"
                        />
                        <span className="sr-only">
                            Present Day Counseling and Recovery
                        </span>
                    </Link>

                    {/* Desktop nav */}
                    <nav
                        className="hidden md:flex items-center gap-8"
                        data-testid="desktop-nav"
                    >
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/"}
                                className={({ isActive }) =>
                                    `relative text-[15px] font-medium tracking-wide transition-colors duration-200 ${
                                        isActive
                                            ? "text-[#2C332A]"
                                            : "text-[#5B6358] hover:text-[#2C332A]"
                                    }`
                                }
                                data-testid={`nav-link-${item.label.toLowerCase()}`}
                            >
                                {({ isActive }) => (
                                    <>
                                        {item.label}
                                        <span
                                            className={`absolute -bottom-1 left-0 h-[2px] bg-[#8C9C88] transition-all duration-300 ${
                                                isActive ? "w-full" : "w-0"
                                            }`}
                                        />
                                    </>
                                )}
                            </NavLink>
                        ))}
                        <Link
                            to="/consultation"
                            className="rounded-full bg-[#8C9C88] text-white px-5 py-2.5 text-sm font-medium tracking-wide shadow-sm transition-all duration-300 hover:bg-[#7A8A76] hover:-translate-y-0.5 hover:shadow-md"
                            data-testid="nav-book-cta"
                        >
                            Book a Consultation
                        </Link>
                    </nav>

                    {/* Mobile hamburger */}
                    <button
                        onClick={() => setOpen((v) => !v)}
                        className={`md:hidden hamburger ${open ? "open" : ""}`}
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                        data-testid="mobile-menu-toggle"
                    >
                        <span></span>
                        <span></span>
                        <span></span>
                    </button>
                </div>
            </div>

            {/* Mobile overlay panel */}
            <div
                className={`md:hidden fixed inset-x-0 top-20 bottom-0 z-50 bg-[#FAF9F6] transition-opacity duration-300 ${
                    open
                        ? "opacity-100 pointer-events-auto"
                        : "opacity-0 pointer-events-none"
                }`}
                data-testid="mobile-nav-panel"
            >
                <nav className="flex flex-col px-6 pt-8 gap-2">
                    {navItems.map((item, idx) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            className={({ isActive }) =>
                                `py-4 border-b border-[#E3DCC8] text-2xl font-heading tracking-tight transition-colors ${
                                    isActive
                                        ? "text-[#2C332A]"
                                        : "text-[#5B6358]"
                                }`
                            }
                            style={{
                                animation: open
                                    ? `pd-fade-up 0.5s ${0.05 * idx + 0.05}s both`
                                    : "none",
                            }}
                            data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                        >
                            {item.label}
                        </NavLink>
                    ))}
                    <Link
                        to="/consultation"
                        className="mt-6 inline-flex justify-center rounded-full bg-[#8C9C88] text-white px-6 py-3 text-base font-medium tracking-wide shadow-sm transition-all duration-300 hover:bg-[#7A8A76]"
                        data-testid="mobile-book-cta"
                    >
                        Book a Consultation
                    </Link>
                    <a
                        href="tel:4052540805"
                        className="mt-3 inline-flex justify-center rounded-full border-2 border-[#8C9C88] text-[#8C9C88] px-6 py-3 text-base font-medium tracking-wide transition-all duration-300 hover:bg-[#8C9C88] hover:text-white"
                        data-testid="mobile-call-cta"
                    >
                        Call 405-254-0805
                    </a>
                </nav>
            </div>
        </header>
    );
}
